import { AutoAwesome } from "@mui/icons-material";
import { Box, Stack } from "@mui/material";
import { useFeatureGate } from "@statsig/react-bindings";
import dayjs, { Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { FeatureFlagged } from "@/Components/FeatureFlagging";
import { Alert } from "@/Components/v2/atoms/Alert";
import { HierarchicalAutocomplete } from "@/Components/v2/atoms/Autocomplete";
import { HierarchicalOption } from "@/Components/v2/atoms/Autocomplete/types";
import { PrimaryButton, WarcButton } from "@/Components/v2/atoms/Button";
import { BrainIcon } from "@/Components/v2/atoms/Icon/BrainIcon";
import { InputAdornment } from "@/Components/v2/atoms/InputAdornment";
import { TextField } from "@/Components/v2/atoms/TextField";
import { Typography } from "@/Components/v2/atoms/Typography";
import {
  calculateDateRangePresets,
  DateRangePicker,
} from "@/Components/v2/organism/DateRangePicker";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { formatDate } from "@/shared/utils/formatDate";
import { NodeHierarchy } from "@/shared/utils/nodes";

import { toast } from "react-toastify";
import { UI_CONSTANTS } from "../../constants";
import { useChat } from "../../context/ChatContext";
import { DocumentUpload } from "./DocumentUpload";
import { StatusAlert } from "./StatusAlert";
import { WarcTooltip } from "./WarcTooltip";

type PromptBuilderForm = {
  businessUnit: string;
  startDate: string;
  endDate: string;
  question: string;
};

type RangeValue = [Dayjs | null, Dayjs | null] | null;

interface PromptBuilderProps {
  preSetConstraints?: {
    brand?: string;
    category?: string;
    product?: string;
    startDate?: string;
    endDate?: string;
  };
}

export const PromptBuilder = ({ preSetConstraints }: PromptBuilderProps) => {
  const { metadata, isAsking, askQuestion } = useChat();
  const { value: isWarcFeatureEnabled } = useFeatureGate(featureFlag.enableWarc);
  const warcEnabledForTier = isWarcFeatureEnabled && metadata?.hendrenTier === "PREMIUM";
  const [warcEnabled, setWarcEnabled] = useState<boolean>(warcEnabledForTier);
  const { nodes, tree } = useInfo();
  const queryLimitReached = metadata?.queryLimitReached || false;
  const remainingQueries = metadata?.remainingQueries || 0;
  const isFreeUser = metadata?.hendrenTier === "FREE";

  const extra = tree?.getNodeTreeExtras();
  const minDate = extra && dayjs(extra[0]?.min_date);
  const maxDate = extra && dayjs(extra[0]?.max_date);
  const defaultEndDate = maxDate;
  const defaultStartDate = maxDate?.subtract(12, "w");
  const formmattedMinDate = dayjs(minDate);
  const formmattedMaxDate = dayjs(maxDate);

  // Find the node that matches the preset constraints
  const initialBusinessUnit = useMemo(() => {
    if (!preSetConstraints || !nodes) return "";

    const { brand, category, product } = preSetConstraints;
    if (!brand || !category || !product) return "";

    const matchingNode = nodes.findNodeByBrandCategoryProduct(brand, category, product);
    return matchingNode?.node.uuid || "";
  }, [preSetConstraints, nodes]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PromptBuilderForm>({
    mode: "onChange",
    defaultValues: {
      businessUnit: nodes?.getHierarchy()[0]?.uuid || "",
      startDate: defaultStartDate ? formatDate(defaultStartDate) : "",
      endDate: defaultEndDate ? formatDate(defaultEndDate) : "",
      question: "",
    },
  });

  const businessUnitHierarchyOptions: HierarchicalOption[] = useMemo(() => {
    if (!nodes) return [];

    const rootNodeId = 0;
    const mapNodeToOption = (node: NodeHierarchy): HierarchicalOption => ({
      id: node.id,
      label: node.label,
      value: node.uuid,
      children: node.children.map(mapNodeToOption),
      alwaysOpen: node.id === rootNodeId,
    });

    const hierarchicalOptions = nodes.getHierarchy();
    return hierarchicalOptions.map(mapNodeToOption);
  }, [nodes]);

  const selectedBusinessUnit = watch("businessUnit");
  const startDate = watch("startDate");
  const endDate = watch("endDate");

  // Get the selected node
  const selectedNode = useMemo(() => {
    if (!selectedBusinessUnit || !nodes) return null;
    return nodes.getNode(selectedBusinessUnit);
  }, [selectedBusinessUnit, nodes]);

  const selectedNodeHierarchy = useMemo(() => {
    if (!selectedBusinessUnit || !nodes) return null;

    const ancestors = nodes.getNodeAndAncestors(selectedBusinessUnit);
    if (ancestors.length === 0) return null;

    return ancestors
      .reverse()
      .map((node) => node.warchestNode.extras.mappedName || node.warchestNode.name)
      .join(` > `);
  }, [selectedBusinessUnit, businessUnitHierarchyOptions]);

  const onSubmit = (data: PromptBuilderForm) => {
    if (!selectedNode) {
      toast.error("No business unit selected");
      return;
    }

    const brand = selectedNode.node.brand ?? "default";
    const category = selectedNode.node.category ?? "default";
    const product = selectedNode.node.product ?? "default";

    askQuestion(data.question, warcEnabled, {
      startDate: data.startDate,
      endDate: data.endDate,
      brand,
      category,
      product,
    });
  };

  const handleDateRangeChange = (newRange: RangeValue) => {
    if (!newRange) return;

    const [newStartDayjs, newEndDayjs] = newRange;
    if (!newStartDayjs || !newEndDayjs) return;

    const newStart = formatDate(newStartDayjs);
    const newEnd = formatDate(newEndDayjs);
    if (newStart && newEnd) {
      setValue("startDate", newStart, { shouldValidate: true });
      setValue("endDate", newEnd, { shouldValidate: true });
    }
  };

  const tooltipMessage = useMemo(() => {
    if (!warcEnabledForTier) {
      return "WARC References are a premium feature. Contact us for more information.";
    }
    if (isAsking) {
      return "MAITE may use WARC to answer your question";
    }

    return warcEnabled
      ? "WARC is enabled. MAITE may use WARC to answer your question."
      : "WARC is disabled. Questions will be grounded in your MMM data and general knowledge.";
  }, [warcEnabledForTier, warcEnabled, isAsking]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack direction="column" gap={2}>
        <Alert
          severity="info"
          sx={{
            backgroundColor: (theme) => theme.palette.infoBanner.lightBackground,
          }}
          icon={<AutoAwesome sx={{ fontSize: "16px", color: "mutinexMagenta.main" }} />}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Prompt Builder
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 400 }}>
            Prompt Builder helps you get the most out of MAITE by guiding you to create precise
            queries. Simply add parameters for the metrics, dates, and channels you want to analyze.
            MAITE will then generate a response and all future analyses in this chat based on these
            parameters.
          </Typography>
        </Alert>
        <Stack direction="column" gap={1}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
            What business unit are you interested in?
          </Typography>
          <Box sx={{ minWidth: "300px", width: "fit-content" }}>
            <Controller
              name="businessUnit"
              control={control}
              rules={{ required: "Please select a business unit" }}
              render={({ field }) => (
                <HierarchicalAutocomplete
                  sx={{
                    "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
                      paddingLeft: "12px",
                      borderRadius: "4px",
                    },
                    minWidth: "100%",
                  }}
                  id="businessUnit"
                  disableClearable
                  disabled={queryLimitReached}
                  options={businessUnitHierarchyOptions}
                  value={field.value}
                  onChange={(value) => field.onChange(value?.value)}
                  renderInput={(params) => (
                    <TextField
                      {...(params as any)}
                      inputProps={{
                        ...params.inputProps,
                        value: "",
                      }}
                      size="small"
                      color="primary"
                      error={!!errors.businessUnit}
                      helperText={errors.businessUnit?.message}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography
                              fontSize="16px"
                              fontWeight="400"
                              sx={{ color: "text.primary" }}
                            >
                              {selectedNodeHierarchy
                                ? `${selectedNodeHierarchy}`
                                : "Select a Business Unit"}
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          </Box>
        </Stack>

        <Stack direction="column" gap={1}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
            What dates would you like to cover?
          </Typography>

          <Stack direction="row" gap={2}>
            <DateRangePicker
              value={[startDate ? dayjs(startDate) : null, endDate ? dayjs(endDate) : null]}
              onChange={handleDateRangeChange}
              presets={calculateDateRangePresets(formmattedMinDate, formmattedMaxDate) || []}
              allowClear={false}
              size="large"
              minDate={formmattedMinDate}
              maxDate={formmattedMaxDate}
              format={dayjs.localeData().longDateFormat("L")}
              name="dateRange"
              disabled={queryLimitReached}
            />
          </Stack>
        </Stack>

        <Stack direction="column" gap={1}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
            Enter your question:
          </Typography>
          <Controller
            name="question"
            control={control}
            rules={{
              required: "Please enter a question",
              minLength: {
                value: 1,
                message: "Question must be at least 1 character long",
              },
            }}
            render={({ field }) => (
              <Box
                position="relative"
                mt="auto"
                borderRadius={3}
                border={1}
                borderColor={errors.question ? "error.main" : "divider"}
                p={2}
                display="flex"
                flexDirection="column"
                gap={1.25}
              >
                <TextField
                  multiline
                  rows={UI_CONSTANTS.TEXTAREA_ROWS}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit(onSubmit)();
                    }
                  }}
                  fullWidth
                  placeholder="What were my best performing channels?"
                  InputProps={{ disableUnderline: true }}
                  variant="standard"
                  inputRef={field.ref}
                  error={!!errors.question}
                  disabled={queryLimitReached}
                  sx={{
                    "& .MuiInputBase-root": { alignItems: "flex-start" },
                    "& textarea": {
                      p: 0,
                      m: 0,
                      lineHeight: 1.5,
                      resize: "none",
                    },
                  }}
                />
                {errors.question && (
                  <Typography variant="caption" color="error.main" sx={{ mt: 0.5 }}>
                    {errors.question.message}
                  </Typography>
                )}
              </Box>
            )}
          />
        </Stack>
        <Stack direction="column" gap={1}>
          <StatusAlert
            severity="info"
            sx={{
              backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#2A3A4B" : "#E5F2F9"),
            }}
          >
            <Typography variant="body1" component="div" sx={{ fontWeight: 400 }}>
              <Box component="span" sx={{ fontWeight: 600 }}>
                Note:
              </Box>{" "}
              Breakdowns at the individual brand, product line or business line are less reliable
              when you select the portfolio view. For accurate results, choose a single brand,
              product or business line in the business selector before asking a detailed question
            </Typography>
          </StatusAlert>
          <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
            Reference any internal documents or external sources?
          </Typography>
          <Stack
            direction="row"
            gap={2}
            alignItems="center"
            justifyContent="flex-start"
            sx={{ minHeight: "40px" }}
          >
            <DocumentUpload hasPendingAction={false} disabled={queryLimitReached} />
            <FeatureFlagged featureName="enableWarc">
              <WarcTooltip message={tooltipMessage} placement="top">
                <WarcButton
                  onClick={
                    warcEnabledForTier && !queryLimitReached
                      ? () => setWarcEnabled((prev) => !prev)
                      : undefined
                  }
                  isFeatureEnabled={warcEnabledForTier}
                  disabled={queryLimitReached}
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      gap: "6px",
                      px: "10px",
                      py: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <BrainIcon
                      status={isAsking ? "pending" : warcEnabled ? "enabled" : "disabled"}
                      disabled={!warcEnabledForTier}
                      sx={{
                        width: "20px",
                        height: "20px",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "12px",
                        fontWeight: 500,
                        lineHeight: 1,
                        margin: 0,
                        padding: 0,
                      }}
                    >
                      WARC
                    </Typography>
                  </Stack>
                </WarcButton>
              </WarcTooltip>
            </FeatureFlagged>
          </Stack>
        </Stack>

        {/* Query limit alerts */}
        {!queryLimitReached && isFreeUser && (
          <StatusAlert
            title={`You have ${remainingQueries} MAITE ${remainingQueries === 1 ? "query" : "queries"} remaining`}
            severity="info"
          >
            Talk to your Marketing Science Partner to unlock full MAITE access. Get unlimited
            queries, chat history, and much more.
          </StatusAlert>
        )}

        {queryLimitReached && (
          <StatusAlert title="You've used all your free queries for this month" severity="warning">
            Talk to your Marketing Science Partner to unlock full MAITE access. Get unlimited
            queries, chat history, and much more.
          </StatusAlert>
        )}

        <PrimaryButton type="submit" disabled={isAsking || queryLimitReached}>
          Generate my insights
        </PrimaryButton>
      </Stack>
    </form>
  );
};
